import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import type { CostReport, EmploymentType } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEmployeesApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';

const COLUMN_COUNT = 5;

function toCsv(report: CostReport): string {
  const header = ['Employee', 'Type', 'RateType', 'RateAmount', 'Hours', 'MonthlyCost', 'Note'];
  const rows = report.entries.map((r) => [
    r.fullName,
    r.employmentType ?? '',
    r.rateType ?? '',
    r.rateAmount ?? '',
    r.hours ?? '',
    r.monthlyCost,
    r.note ?? '',
  ]);
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    return s.includes(';') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [header, ...rows].map((row) => row.map(escape).join(';')).join('\n');
}

interface EmployeeCostReportProps {
  companyNip: string;
  companyName: string;
}

export function EmployeeCostReport({ companyNip, companyName }: EmployeeCostReportProps) {
  const { costReport } = useEmployeesApi();
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth() + 1);
  const [report, setReport] = useState<CostReport | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costReport(companyNip, year, month)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((err) => { if (!cancelled) toast.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyNip, year, month, costReport]);

  const yearOptions = Array.from({ length: 7 }, (_, i) => {
    const y = today.getUTCFullYear() - 4 + i;
    return { value: y, label: String(y) };
  });
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: t(`employees.hours.months.${i + 1}`),
  }));

  const handleExport = () => {
    if (!report) return;
    const csv = toCsv(report);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-report-${companyNip}-${year}-${String(month).padStart(2, '0')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const monthLabel = t(`employees.hours.months.${month}`);

  return (
    <div className="flex flex-col gap-4 px-6 py-4">
      <h3 className="text-lg font-semibold">
        {t('employees.costReport.titleWithMonth', { month: monthLabel, year: String(year) })}
        {companyName ? ` — ${companyName}` : ''}
      </h3>

      <div className="flex items-center gap-2">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((opt) => (
              <SelectItem key={opt.value} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((opt) => (
              <SelectItem key={opt.value} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={handleExport} disabled={!report}>
          <Download className="h-4 w-4" />
          {t('employees.costReport.exportCsv')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('employees.costReport.employee')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('employees.type.label')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('employees.rateType.label')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap text-right">
                {t('employees.rates.amount')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap text-right">
                {t('employees.hours.hours')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap text-right">
                {t('employees.costReport.monthlyCost')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: COLUMN_COUNT }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              (report?.entries ?? []).map((row) => (
                <TableRow key={row.employeeId}>
                  <TableCell>{row.fullName}</TableCell>
                  <TableCell>
                    {row.employmentType
                      ? t(`employees.type.${row.employmentType}`)
                      : t('employees.type.notSet')}
                  </TableCell>
                  <TableCell>{row.rateType ? t(`employees.rateType.${row.rateType}`) : '—'}</TableCell>
                  <TableCell className="text-right">
                    {row.rateAmount !== null ? formatPLN(row.rateAmount) : '—'}
                  </TableCell>
                  <TableCell className="text-right">{row.hours !== null ? row.hours : '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span>{formatPLN(row.monthlyCost)} PLN</span>
                      {row.note === 'no_active_rate' && (
                        <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400">
                          {t('employees.costReport.noActiveRate')}
                        </Badge>
                      )}
                      {row.note === 'missing_hours' && (
                        <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                          {t('employees.costReport.missingHours')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {report && (
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.costReport.totalByType')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {(Object.entries(report.totals.byType) as [EmploymentType, number][]).map(([type, amount]) => (
              <div key={type}>
                {t(`employees.type.${type}`)}: <strong>{formatPLN(amount)} PLN</strong>
              </div>
            ))}
            <div className="mt-2 text-lg font-semibold">
              {t('employees.costReport.grandTotal')}: {formatPLN(report.totals.grandTotal)} PLN
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
